import React, { useEffect, useMemo, useState } from "react";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import { getAllStores } from "../fefoApi";
import FefoDialog from "./FefoDialog";

/** Nomes iguais aos do `FiltrosDto` do backend (`whitelist: true` descarta o resto). */
export interface ReportFilters {
  inicio: string;
  fim: string;
  lojas: string[];
}

export const EMPTY_REPORT_FILTERS: ReportFilters = { inicio: "", fim: "", lojas: [] };

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportType: "FEFO" | "SHELF";
  /** Filtros da última geração: reabrir o diálogo mantém a escolha anterior. */
  filters: ReportFilters;
  onGenerateReport: (filters: ReportFilters) => Promise<void>;
}

interface Store {
  id: string;
  nomeFantasia: string;
}

const ReportModal: React.FC<ReportModalProps> = ({ isOpen, onClose, reportType, filters, onGenerateReport }) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [storesError, setStoresError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<ReportFilters>(filters);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setDraft(filters);
    setSearch("");
    setLoadingStores(true);
    setStoresError(null);
    getAllStores()
      .then((list) => setStores([...list].sort((a, b) => a.nomeFantasia.localeCompare(b.nomeFantasia, "pt-BR"))))
      .catch((err) => setStoresError(err instanceof Error ? err.message : "Erro ao carregar as lojas."))
      .finally(() => setLoadingStores(false));
    // `filters` só é lido na abertura.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const visibleStores = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("pt-BR");
    return term ? stores.filter((s) => s.nomeFantasia.toLocaleLowerCase("pt-BR").includes(term)) : stores;
  }, [stores, search]);

  const toggleStore = (id: string) =>
    setDraft((prev) => ({
      ...prev,
      lojas: prev.lojas.includes(id) ? prev.lojas.filter((x) => x !== id) : [...prev.lojas, id],
    }));

  const periodInvalid = Boolean(draft.inicio && draft.fim && draft.fim < draft.inicio);

  const handleSubmit = async () => {
    if (periodInvalid) return;
    setGenerating(true);
    try {
      await onGenerateReport(draft);
      onClose();
    } catch {
      // A página já avisou com toast; o diálogo fica aberto para tentar de novo.
    } finally {
      setGenerating(false);
    }
  };

  const label = reportType === "FEFO" ? "FEFO" : "Shelf";

  return (
    <FefoDialog
      isOpen={isOpen}
      wide
      title={`Relatório ${label}`}
      description="Escolha o período e as lojas. Sem loja marcada, o relatório inclui todas."
      onClose={() => {
        if (!generating) onClose();
      }}
      actions={
        <>
          <button type="button" className="ecoBtn" onClick={onClose} disabled={generating}>
            Cancelar
          </button>
          <button
            type="button"
            className="ecoBtn ecoBtn--primary"
            onClick={handleSubmit}
            disabled={generating || periodInvalid}
          >
            <EcoIcon name="download" size={18} />
            {generating ? "Gerando..." : "Baixar Excel"}
          </button>
        </>
      }
    >
      <div className="fefoForm">
        <div className="fefoForm__row">
          <div className="ecoField">
            <label htmlFor="fefo-rel-inicio">Data inicial</label>
            <input
              id="fefo-rel-inicio"
              className="ecoInput"
              type="date"
              value={draft.inicio}
              onChange={(e) => setDraft((prev) => ({ ...prev, inicio: e.target.value }))}
            />
          </div>
          <div className="ecoField">
            <label htmlFor="fefo-rel-fim">Data final</label>
            <input
              id="fefo-rel-fim"
              className="ecoInput"
              type="date"
              value={draft.fim}
              min={draft.inicio || undefined}
              onChange={(e) => setDraft((prev) => ({ ...prev, fim: e.target.value }))}
            />
          </div>
        </div>
        {periodInvalid ? (
          <p className="fefoError" role="alert">
            A data final é anterior à inicial.
          </p>
        ) : null}

        <div className="ecoField">
          <span className="ecoLabel" id="fefo-rel-lojas">
            Lojas{" "}
            <span className="ecoLabel__optional">
              {draft.lojas.length ? `(${draft.lojas.length} marcada${draft.lojas.length === 1 ? "" : "s"})` : "(todas)"}
            </span>
          </span>
          <div className="fefoStorePicker" role="group" aria-labelledby="fefo-rel-lojas">
            <div className="ecoSearch">
              <EcoIcon name="search" size={18} className="ecoSearch__icon" />
              <input
                type="search"
                className="ecoInput ecoInput--sm"
                placeholder="Buscar loja"
                aria-label="Buscar loja"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="fefoStorePicker__list">
              {loadingStores ? (
                <p className="fefoStorePicker__empty">Carregando lojas...</p>
              ) : storesError ? (
                <p className="fefoError">{storesError}</p>
              ) : visibleStores.length === 0 ? (
                <p className="fefoStorePicker__empty">Nenhuma loja encontrada.</p>
              ) : (
                visibleStores.map((store) => (
                  <label key={store.id} className="fefoStoreOption">
                    <input
                      type="checkbox"
                      checked={draft.lojas.includes(store.id)}
                      onChange={() => toggleStore(store.id)}
                    />
                    <span>{store.nomeFantasia}</span>
                  </label>
                ))
              )}
            </div>
            {draft.lojas.length ? (
              <button
                type="button"
                className="ecoLinkBtn fefoStorePicker__clear"
                onClick={() => setDraft((prev) => ({ ...prev, lojas: [] }))}
              >
                Desmarcar todas
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </FefoDialog>
  );
};

export default ReportModal;
